import { EntryPass } from '@/constants'
import { useToast } from './use-toast'
import { apiFetch } from '@/utils'
import { useCallback, useEffect, useState } from 'react'

export const useGetActiveEntryPass = (walletAddress: `0x${string}`) => {
  const { toast } = useToast()
  const [activeEntryPass, setActiveEntryPass] = useState<EntryPass | null>(null)
  const [isEntryPassLoading, setIsEntryPassLoading] = useState(true)
  const [hasFetched, setHasFetched] = useState(false)
  const [activeEntryPasses, setActiveEntryPasses] = useState<EntryPass[]>([])

  const fetchActiveEntryPass = useCallback(async (): Promise<EntryPass | null> => {
    try {
      setIsEntryPassLoading(true)
      const response = await apiFetch(`/v1/entry-passes/${walletAddress}`, {
        withCredentials: true
      })

      if (response.status === 404) {
        // No active pass — not an error
        setActiveEntryPass(null)
        return null
      }

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(
          errorData.details ||
            errorData.message ||
            `HTTP error! status: ${response.status}`
        )
      }

      const data = await response.json()

      // Handle both direct pass object and wrapped { success, data } responses
      if (data && data.success !== undefined && data.data !== undefined) {
        setActiveEntryPass(data.data || null)
        return data.data || null
      } else {
        setActiveEntryPass(data || null)
        return data || null
      }
    } catch (error: any) {
      console.error('Error fetching active entry pass. Reason: ', error)
      setActiveEntryPass(null)
      toast({
        title: 'Can not fetch active entry pass',
        description:
          error?.message || 'An error occurred while fetching active entry pass'
      })
      return null
    } finally {
      setIsEntryPassLoading(false)
      setHasFetched(true)
    }
  }, [walletAddress, toast])

  // Fetch all active passes from history endpoint
  const fetchAllActivePasses = useCallback(async () => {
    try {
      const res = await apiFetch(`/v1/entry-passes/${walletAddress}/history`, {
        withCredentials: true
      })
      if (res.ok) {
        const allPasses: EntryPass[] = await res.json()
        setActiveEntryPasses(allPasses.filter(p => p.status === 'active'))
      }
    } catch {
      // Non-critical — activeEntryPasses just won't be populated
    }
  }, [walletAddress])

  const refetch = useCallback(async (): Promise<EntryPass | null> => {
    const pass = await fetchActiveEntryPass()
    await fetchAllActivePasses()
    return pass
  }, [fetchActiveEntryPass, fetchAllActivePasses])

  useEffect(() => {
    if (walletAddress && !hasFetched) {
      fetchActiveEntryPass()
      fetchAllActivePasses()
    }
  }, [walletAddress, hasFetched, fetchActiveEntryPass, fetchAllActivePasses])

  return {
    activeEntryPass,
    activeEntryPasses,
    isEntryPassLoading,
    refetch
  }
}
